import React, { useEffect, useMemo, useState } from "react";
import DataCenterShortcuts from "@/components/datacenter/DataCenterShortcuts";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Plus, Search, ChevronLeft, ChevronRight, ArrowDownWideNarrow, Inbox } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import ColumnPicker from "@/components/admin/crud/ColumnPicker";
import RowActions from "@/components/admin/crud/RowActions";
import CellValue from "@/components/admin/crud/CellValue";
import { publicUrlFor, humanize, singular, titleOf, defaultColumns, allColumnOptions, isMediaField } from "@/components/admin/crud/crudUtils";
import { absMediaUrl } from "@/components/marketplace/MediaPicker";

const colKey = (entity) => `gcw_admin_cols_${entity}`;

function RecordCell({ row }) {
  const img = Object.keys(row).find((k) => isMediaField(k) && typeof row[k] === "string" && row[k]);
  const title = titleOf(row);
  const sub = row.email && row.email !== title ? row.email : row.slug || String(row.id || "").slice(0, 12);
  return (
    <div className="flex items-center gap-3 min-w-0">
      {img
        ? <img src={absMediaUrl(row[img])} alt="" className="h-9 w-9 shrink-0 rounded-xl object-cover ring-1 ring-slate-200" />
        : <div className="h-9 w-9 shrink-0 rounded-xl bg-gradient-to-br from-[#165a71]/15 to-[#5fd3c6]/20 text-[#165a71] grid place-items-center text-sm font-semibold">{title.charAt(0).toUpperCase()}</div>}
      <div className="min-w-0">
        <div className="font-medium text-[#071b22] truncate max-w-[280px] group-hover:text-[#165a71] transition-colors">{title}</div>
        <div className="text-xs text-slate-400 truncate max-w-[280px]">{sub}</div>
      </div>
    </div>
  );
}

export default function AdminCRUDPage() {
  const { entity } = useParams();
  const [searchParams] = useSearchParams();
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [sort, setSort] = useState("-created_at");
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    setMeta(null); setOffset(0); setQ(""); setStatusFilter(searchParams.get("status") || "");
    api.get("/admin/crud/entities").then(({ data }) => {
      const found = (data || []).find((e) => e.key === entity);
      if (!found) { toast.error("Unknown entity or not authorized"); return; }
      let saved = null;
      try { saved = JSON.parse(window.localStorage.getItem(colKey(entity)) || "null"); } catch (_) {}
      setColumns(Array.isArray(saved) ? saved : defaultColumns(found));
      setMeta(found);
    }).catch(() => toast.error("Failed to load entity list"));
    // eslint-disable-next-line
  }, [entity]);

  const load = async () => {
    if (!meta) return;
    setBusy(true);
    try {
      const params = { q: q || undefined, status: statusFilter || undefined, sort, limit, offset, include_deleted: includeDeleted };
      const { data } = await api.get(`/admin/crud/${entity}`, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSelectedIds([]);
    } catch (err) { toast.error(err?.response?.data?.error || "Load failed"); } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [meta, q, statusFilter, sort, offset, includeDeleted]);

  const columnOptions = useMemo(() => allColumnOptions(meta, items).filter(Boolean), [meta, items]);
  const updateColumns = (cols) => { setColumns(cols); window.localStorage.setItem(colKey(entity), JSON.stringify(cols)); };
  const resetColumns = () => { window.localStorage.removeItem(colKey(entity)); setColumns(defaultColumns(meta)); };

  const toggleAll = () => setSelectedIds(selectedIds.length === items.length ? [] : items.map((i) => i.id));
  const toggleOne = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const fail = (err) => toast.error(err?.response?.data?.error || err?.response?.data?.detail || "Failed");

  const changeStatus = async (id, newStatus) => {
    const reason = window.prompt(`Reason for changing status to "${newStatus}"? (optional)`);
    if (reason === null) return;
    try { await api.post(`/admin/crud/${entity}/${id}/status`, { status: newStatus, reason: reason || undefined }); toast.success("Status updated"); load(); } catch (err) { fail(err); }
  };
  const bulkStatus = async (nxt) => {
    if (!nxt || !selectedIds.length) return;
    try { await api.post(`/admin/crud/${entity}/bulk/status`, { ids: selectedIds, status: nxt }); toast.success(`Updated ${selectedIds.length} record(s)`); load(); } catch (err) { fail(err); }
  };
  const softDelete = async (id) => {
    if (!window.confirm("Move this record to trash?")) return;
    try { await api.delete(`/admin/crud/${entity}/${id}`); toast.success("Moved to trash"); load(); } catch (err) { fail(err); }
  };
  const restore = async (id) => { try { await api.post(`/admin/crud/${entity}/${id}/restore`); toast.success("Restored"); load(); } catch (err) { fail(err); } };
  const hardDelete = async (id) => {
    const c = window.prompt(`Type the ID to confirm PERMANENT deletion: ${id}`);
    if (c !== id) return;
    try { await api.delete(`/admin/crud/${entity}/${id}/permanent`, { params: { confirm: id } }); toast.success("Deleted permanently"); load(); } catch (err) { fail(err); }
  };
  const actAs = async (row) => {
    if (!window.confirm(`Act as ${row.email || row.display_name || row.id}? You'll be signed into their account for 360° support.`)) return;
    try { await startImpersonation(row.id); toast.success("Now acting as user — support mode on"); navigate("/dashboard"); } catch (err) { fail(err); }
  };
  const exportCsv = () => {
    const token = window.localStorage.getItem("gcw_bearer") || "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/crud/${entity}/export.csv?q=${encodeURIComponent(q || "")}&status=${encodeURIComponent(statusFilter || "")}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.blob()).then((blob) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob); link.download = `${entity}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
    }).catch(() => toast.error("Export failed"));
  };

  if (!meta) return <div className="text-slate-500 p-6" data-testid="admin-crud-loading">Loading…</div>;
  const transitions = meta.status_field ? meta.status_transitions || [] : [];
  const editUrl = (id) => `/admin/entities/${entity}/${id}`;
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="max-w-full" data-testid={`admin-crud-${entity}`}>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">{meta.group}</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">{meta.label}</h1>
          <div className="text-sm text-slate-500 mt-1.5" data-testid="crud-total">{total.toLocaleString()} record{total === 1 ? "" : "s"}{statusFilter ? ` · ${statusFilter.replace(/_/g, " ")}` : ""}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DataCenterShortcuts entity={entity} />
          {meta.creatable && <button onClick={() => navigate(`/admin/entities/${entity}/new`)} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-semibold shadow-sm hover:bg-[#0f4657] transition-colors" data-testid="crud-new-btn"><Plus className="h-4 w-4" /> New {singular(meta.label).toLowerCase()}</button>}
          <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-[#071b22] hover:border-[#165a71] transition-colors" data-testid="crud-export"><Download className="h-4 w-4" /> Quick CSV</button>
        </div>
      </div>

      {transitions.length > 0 && (
        <div className="flex items-center gap-1 overflow-x-auto mb-4 border-b border-slate-200" data-testid="crud-status-filter">
          {["", ...transitions].map((s) => (
            <button key={s || "all"} onClick={() => { setOffset(0); setStatusFilter(s); }}
              className={`relative whitespace-nowrap px-3.5 py-2.5 text-sm capitalize transition-colors ${statusFilter === s ? "text-[#071b22] font-semibold" : "text-slate-500 hover:text-[#071b22]"}`}
              data-testid={`crud-status-tab-${s || "all"}`}>
              {s ? s.replace(/_/g, " ") : "All"}
              {statusFilter === s && <span className="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-[#165a71]" />}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => { setOffset(0); setQ(e.target.value); }} placeholder={`Search ${meta.label.toLowerCase()}…`}
            className="w-full rounded-full border border-slate-200 bg-white pl-10 pr-4 py-2 text-sm outline-none transition-[border-color,box-shadow] focus:border-[#165a71] focus:ring-4 focus:ring-[#165a71]/10" data-testid="crud-search" />
        </div>
        <div className="relative">
          <ArrowDownWideNarrow className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="appearance-none rounded-full border border-slate-200 bg-white pl-9 pr-4 py-2 text-sm text-[#071b22] outline-none focus:border-[#165a71]" data-testid="crud-sort">
            {(meta.sort_fields || ["created_at"]).flatMap((f) => [`-${f}`, f]).map((s) => (
              <option key={s} value={s}>{humanize(s)} {s.startsWith("-") ? "(newest/high)" : "(oldest/low)"}</option>
            ))}
          </select>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-500 px-2">
          <Switch checked={includeDeleted} onCheckedChange={setIncludeDeleted} data-testid="crud-show-deleted" /> Show trash
        </label>
        <div className="ml-auto"><ColumnPicker options={columnOptions} selected={columns} onChange={updateColumns} onReset={resetColumns} /></div>
      </div>

      {selectedIds.length > 0 && (
        <div className="mb-3 flex items-center gap-3 rounded-2xl bg-[#071b22] text-white px-4 py-2.5 text-sm animate-in fade-in slide-in-from-top-1" data-testid="crud-bulk-bar">
          <span className="font-medium">{selectedIds.length} selected</span>
          {transitions.length > 0 && (
            <select defaultValue="" onChange={(e) => { bulkStatus(e.target.value); e.target.value = ""; }} className="rounded-full bg-white/10 border border-white/20 px-3 py-1 text-sm text-white capitalize" data-testid="crud-bulk-status">
              <option value="" className="text-black">Set status…</option>
              {transitions.map((s) => <option key={s} value={s} className="text-black">{s.replace(/_/g, " ")}</option>)}
            </select>
          )}
          <button onClick={() => setSelectedIds([])} className="ml-auto text-white/70 hover:text-white" data-testid="crud-bulk-clear">Clear</button>
        </div>
      )}

      <div className="rounded-3xl border border-slate-200/80 bg-white overflow-hidden shadow-[0_1px_2px_rgba(7,27,34,0.04)]" data-testid="crud-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="pl-5 pr-2 py-3.5 w-10"><Checkbox checked={items.length > 0 && selectedIds.length === items.length} onCheckedChange={toggleAll} data-testid="crud-select-all" /></th>
                <th className="px-3 py-3.5">{singular(meta.label)}</th>
                {columns.map((f) => <th key={f} className="px-3 py-3.5 whitespace-nowrap">{humanize(f)}</th>)}
                <th className="pr-5 py-3.5 w-12" />
              </tr>
            </thead>
            <tbody>
              {busy && items.length === 0 ? (
                [...Array(5)].map((_, i) => <tr key={i} className="border-t border-slate-50"><td colSpan={columns.length + 3} className="px-5 py-4"><div className="h-6 rounded-lg bg-slate-100 animate-pulse" /></td></tr>)
              ) : items.length === 0 ? (
                <tr><td colSpan={columns.length + 3} className="py-20 text-center" data-testid="crud-empty">
                  <Inbox className="h-8 w-8 mx-auto text-slate-300" />
                  <div className="mt-3 font-medium text-[#071b22]">Nothing here yet</div>
                  <div className="text-sm text-slate-500">Try a different search or status.</div>
                </td></tr>
              ) : items.map((row) => (
                <tr key={row.id} onClick={() => navigate(editUrl(row.id))}
                  className={`group border-t border-slate-100 cursor-pointer transition-colors hover:bg-[#165a71]/[0.03] ${selectedIds.includes(row.id) ? "bg-[#165a71]/[0.05]" : ""} ${row.deleted_at ? "opacity-50" : ""} ${busy ? "opacity-60" : ""}`}
                  data-testid={`crud-row-${row.id}`}>
                  <td className="pl-5 pr-2 py-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={selectedIds.includes(row.id)} onCheckedChange={() => toggleOne(row.id)} data-testid={`crud-check-${row.id}`} /></td>
                  <td className="px-3 py-3"><RecordCell row={row} /></td>
                  {columns.map((f) => <td key={f} className="px-3 py-3 whitespace-nowrap"><CellValue field={f} value={row[f]} statusField={meta.status_field} /></td>)}
                  <td className="pr-5 py-3 text-right">
                    <RowActions row={row} viewUrl={publicUrlFor(entity, row)} transitions={transitions} isUsers={entity === "users"}
                      onEdit={() => navigate(editUrl(row.id))} onActAs={() => actAs(row)} onStatus={(s) => changeStatus(row.id, s)}
                      onDelete={() => softDelete(row.id)} onRestore={() => restore(row.id)} onHardDelete={() => hardDelete(row.id)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 text-sm text-slate-500">
          <div data-testid="crud-range">Showing {items.length ? offset + 1 : 0}–{offset + items.length} of {total.toLocaleString()}</div>
          <div className="flex items-center gap-2">
            <span className="text-xs">Page {page} of {pages}</span>
            <button disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - limit))} className="rounded-full border border-slate-200 p-1.5 hover:border-[#165a71] disabled:opacity-40" aria-label="Previous page" data-testid="crud-prev"><ChevronLeft className="h-4 w-4" /></button>
            <button disabled={offset + limit >= total} onClick={() => setOffset((o) => o + limit)} className="rounded-full border border-slate-200 p-1.5 hover:border-[#165a71] disabled:opacity-40" aria-label="Next page" data-testid="crud-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
